import { Diagnosis, DiagnosisList, DiagnosisStatus, DiagnosisStatusList } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ServiceItemClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type DiagnosisFilter = EntityFilter<Diagnosis>;
export type DiagnosisStatusFilter = EntityFilter<DiagnosisStatus>;

export const useDiagnosisBatchGetQuery = <TSelectData = DiagnosisList,>({
  filter,
  select,
  enabled = true,
  refetchOnWindowFocus = true,
}: {
  filter: DiagnosisFilter;
  select?: (data: DiagnosisList) => TSelectData;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
}) =>
  useQuery({
    queryKey: [
      queryKeys.diagnosis.root,
      queryKeys.diagnosis.list,
      queryKeys.diagnosis.getHash(filter),
    ],
    queryFn: async () => {
      const req = Diagnosis.create(filter);
      const result = await ServiceItemClientService.BatchGetDiagnosis(req);
      return result ?? DiagnosisList.create();
    },
    select,
    enabled,
    refetchOnWindowFocus,
  });

export const useDiagnosisGetQuery = ({ id, enabled = true }: { id: number; enabled?: boolean }) =>
  useQuery({
    queryKey: queryKeys.diagnosis.get(id),
    queryFn: async () => {
      const req = Diagnosis.create({ id });
      return ServiceItemClientService.GetDiagnosis(req);
    },
    enabled: enabled && !!id,
  });

export const useDiagnosisCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: Diagnosis) => {
      return ServiceItemClientService.CreateDiagnosis(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.diagnosis.root, queryKeys.diagnosis.list],
      });
    },
  });
};

export const useDiagnosisUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: Diagnosis) => {
      return ServiceItemClientService.UpdateDiagnosis(req);
    },
    onSuccess: (result) => {
      queryClient.setQueriesData<DiagnosisList>(
        { queryKey: [queryKeys.diagnosis.root, queryKeys.diagnosis.list] },
        (oldData) => {
          if (oldData && oldData.results.find((item) => item.id === result.id)) {
            return DiagnosisList.create({
              ...oldData,
              results: oldData.results.map((item) => (item.id === result.id ? result : item)),
            });
          }
        },
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.diagnosis.get(result.id),
      });
    },
  });
};

export const useDiagnosisDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: Diagnosis) => {
      return ServiceItemClientService.DeleteDiagnosis(req);
    },
    onSuccess: (result) => {
      queryClient.setQueriesData<DiagnosisList>(
        { queryKey: [queryKeys.diagnosis.root, queryKeys.diagnosis.list] },
        (oldData) => {
          if (oldData && oldData.results.find((item) => item.id === result.id)) {
            return DiagnosisList.create({
              results: oldData.results.filter((item) => item.id !== result.id),
              totalCount: oldData.totalCount - 1,
            });
          }
        },
      );
      queryClient.invalidateQueries({
        queryKey: [queryKeys.diagnosis.root],
      });
    },
  });
};

export const useDiagnosisStatusBatchGetQuery = <TSelectData = DiagnosisStatusList,>({
  filter,
  select,
  enabled = true,
}: {
  filter: DiagnosisStatusFilter;
  select?: (data: DiagnosisStatusList) => TSelectData;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [
      queryKeys.diagnosisStatus.root,
      queryKeys.diagnosisStatus.list,
      queryKeys.diagnosisStatus.getHash(filter),
    ],
    queryFn: async () => {
      const req = DiagnosisStatus.create(filter);
      const result = await ServiceItemClientService.BatchGetDiagnosisStatus(req);
      return result ?? DiagnosisStatusList.create();
    },
    select,
    enabled,
  });
